/**
 * A mark for a property VALUE that has no fixed vocabulary.
 *
 * ## Why this is not a lookup table
 *
 * Status and priority draw per value from a CLOSED list, so each is a table
 * keyed by the value (`STATUS`, `PriorityMark`). Trade and observation type
 * cannot be: Procore and ACC both let a company define its own trades AND its
 * own observation types, so the vocabulary reaching a card is whatever that
 * company typed — per project, never ours (see
 * `ledai_knowledge/2026-09-30-shared-trade-discipline-vocabulary-spec.md` R2).
 *
 * Both properties were tables before this, and both had the same failure: the
 * table covered the common spellings and everything else fell on one generic
 * mark. Every trade drew the same luggage tag; every one of Procore's ten
 * Environmental observation types drew the same leaf, and a company's own type
 * drew a flag.
 *
 * So this resolves instead of looking up:
 *
 *  1. A LEXICON of icons, each claiming the terms it covers. A value matches
 *     by whole-word prefix, and the most specific phrase wins — so
 *     "Fire Sprinkler" is fire protection while "Fireproofing" is the sprayed
 *     coating, and the two draw different shapes.
 *  2. A MONOGRAM for anything unmatched: the value's initials in a tone
 *     hashed from its name. Stable, distinguishable, and claiming nothing
 *     about what the value means.
 *
 * ## Why the fallback is letters and not a best guess
 *
 * The same reason the extraction filter nulls an off-vocabulary trade rather
 * than taking the nearest match: a confident wrong answer is worse than an
 * honest blank. An icon is read before the word beside it, so a hammer on
 * "Specialty" actively misinforms, while "SP" simply holds the slot and lets
 * the word do the telling.
 *
 * ## Scope
 *
 * Trade, observation type and field names each have their own lexicon.
 * Numbers and identifiers resolve their field's label, rather than its value.
 *
 * The two domains' ICONS must stay disjoint, because one card draws a trade
 * and an observation type at once and two identical shapes on one run name
 * neither of them. `propertyIcons.test.ts` holds that line.
 *
 * Pure and framework-free on purpose: the lexicon is data the tests read
 * directly, and the drawing lives in `PropertyAppearance.tsx`, which maps
 * these names to components.
 */

/**
 * The icons a mark may name. Every one must exist in `lucide-react` AND in
 * `PROPERTY_ICON_COMPONENTS`, which TypeScript enforces by keying that map on
 * this union.
 *
 * No icon may appear in BOTH domains' lexicons: one card carries an
 * observation's type and its trade at once, and two identical shapes on one
 * run name neither of them (`RecordChipRow`, "A mark used twice on one card
 * names neither"). `propertyIcons.test.ts` holds that line.
 */
export type PropertyIconName =
  | "Layers" | "BrickWall" | "Columns3" | "Frame" | "Drill" | "Rows3"
  | "AppWindow" | "House" | "Umbrella" | "Blinds" | "Snowflake"
  | "SquareStack" | "PaintRoller" | "LayoutGrid" | "Footprints" | "Grid2x2" | "DoorOpen"
  | "Zap" | "Cable" | "Droplets" | "AirVent" | "Fan"
  | "FireExtinguisher" | "SprayCan" | "MoveVertical"
  | "Shovel" | "Trees" | "Cone" | "Fence" | "Pickaxe"
  | "HardHat" | "Package" | "Sparkles"
  // Observation type. Disjoint from the trade names above by rule, not by
  // luck — a card draws both at once (`propertyIcons.test.ts`).
  | "Leaf" | "BadgeCheck" | "Gauge" | "ShieldAlert" | "Hammer" | "Wrench"
  | "Wind" | "Landmark" | "Biohazard" | "Volume2" | "Waves" | "CloudRain"
  | "Sprout" | "Recycle" | "Bird"
  | "RotateCcw" | "ClipboardX" | "EqualNot" | "ListChecks"
  | "Bandage" | "TriangleAlert" | "LifeBuoy" | "OctagonAlert" | "Megaphone" | "Siren"
  | "PhoneCall" | "Blend"
  | "UsersRound" | "Hourglass" | "Timer" | "TimerOff" | "Banknote"
  | "PackagePlus" | "PackageMinus" | "Boxes" | "Thermometer" | "Ruler" | "Weight"
  | "Ticket" | "Barcode" | "FileDigit";

/**
 * The tones a mark may take — the eight `sega.css` property tokens and
 * nothing else.
 *
 * A closed set is what keeps this dark-mode correct for free: every one of
 * these is redefined under the dark theme, so a hashed tone can never land on
 * a colour that was only ever checked in light mode. It also keeps a card from
 * turning into confetti — the shape tells trades apart, the tone groups them
 * into families (structure, envelope, finishes, MEP, sitework, life safety).
 */
export type PropertyIconTone = "info" | "green" | "violet" | "orange" | "teal" | "red" | "amber" | "neutral";

export interface PropertyIconLexiconEntry {
  icon: PropertyIconName;
  tone: PropertyIconTone;
  /**
   * Normalized, lowercase terms this icon claims. A term matches when the
   * value's words START with the term's words, consecutively — so "paint"
   * covers "Painting" and "Painter" without a stemmer, while a vowel change
   * ("framing" from "frame") needs its own term.
   */
  terms: string[];
  /**
   * The family this value belongs to, where the vocabulary has families.
   *
   * Observation types are spelled "Category - Type", and the category is a
   * real classification: it decides the TONE, and the reviewer's sheet is read
   * by it (`review-touchups.sim.mjs` asserts one colour and one shape per
   * family). Trade has no such layer and leaves this unset.
   */
  family?: string;
  /**
   * A LAST resort — consulted only when no other entry in the domain matched.
   *
   * The family words need this. Scoring prefers the longer phrase as a proxy
   * for specificity, and that proxy inverts on a label like
   * "Environmental - Noise": the family word is the longest thing in it and
   * the least specific, so it won, and four of Procore's ten Environmental
   * types kept drawing the same leaf. Being a fallback is a fact about the
   * term's MEANING, so the lexicon states it instead of leaving it to be
   * emulated with term lengths.
   */
  fallback?: boolean;
}

/** The vocabularies with a lexicon of their own. */
export type PropertyIconDomain = "trade" | "observationType" | "fieldName";

/**
 * Order matters: it breaks a score tie, so an earlier entry wins a value that
 * names two trades equally ("Framing & Drywall"). Grouped by family, and
 * within a family the trade that leads a combined name comes first.
 */
const TRADE_LEXICON: readonly PropertyIconLexiconEntry[] = [
  // ── Structure ──────────────────────────────────────────────────────────
  { icon: "Layers", tone: "neutral", terms: ["concrete", "cast in place", "slab", "foundation", "footing", "rebar", "reinforcing", "post tension", "gypcrete"] },
  { icon: "BrickWall", tone: "orange", terms: ["masonry", "mason", "brick", "block work", "blockwork", "cmu", "stone", "veneer"] },
  { icon: "Columns3", tone: "neutral", terms: ["structural steel", "steel", "metals", "metal fabrication", "erection", "joist", "girder", "welding"] },
  { icon: "Frame", tone: "orange", terms: ["framing", "rough carpentry", "stud", "truss", "sheathing"] },
  { icon: "Drill", tone: "orange", terms: ["carpentry", "carpenter", "millwork", "casework", "cabinet", "woodwork", "trim work", "finish wood"] },
  { icon: "Rows3", tone: "orange", terms: ["decking", "deck boards", "planking", "boardwalk"] },

  // ── Envelope ───────────────────────────────────────────────────────────
  { icon: "AppWindow", tone: "info", terms: ["curtain wall", "curtainwall", "glazing", "glazier", "glass", "storefront", "window", "skylight"] },
  { icon: "House", tone: "neutral", terms: ["roofing", "roofer", "shingle", "membrane", "built up roof"] },
  { icon: "Umbrella", tone: "info", terms: ["waterproofing", "dampproofing", "sealant", "caulking", "flashing", "weatherproofing", "water proofing"] },
  { icon: "Blinds", tone: "teal", terms: ["cladding", "siding", "metal panel", "stucco", "soffit", "fascia", "exterior finish"] },
  { icon: "Snowflake", tone: "teal", terms: ["insulation", "thermal", "spray foam", "air barrier", "vapor barrier"] },

  // ── Finishes ───────────────────────────────────────────────────────────
  { icon: "SquareStack", tone: "violet", terms: ["drywall", "gypsum", "sheetrock", "taping", "plaster"] },
  { icon: "PaintRoller", tone: "violet", terms: ["paint", "coating", "wall covering", "wallcovering"] },
  { icon: "LayoutGrid", tone: "teal", terms: ["tile", "ceramic", "porcelain", "terrazzo", "grout"] },
  { icon: "Footprints", tone: "orange", terms: ["flooring", "floor covering", "carpet", "resilient", "vinyl", "hardwood"] },
  { icon: "Grid2x2", tone: "neutral", terms: ["acoustical", "acoustic", "ceiling"] },
  { icon: "DoorOpen", tone: "neutral", terms: ["door", "hardware", "frames and hardware", "overhead door", "glass partition"] },

  // ── Mechanical, electrical, plumbing ───────────────────────────────────
  { icon: "Zap", tone: "amber", terms: ["electrical", "electric", "electrician", "lighting", "switchgear"] },
  { icon: "Cable", tone: "violet", terms: ["low voltage", "lowvoltage", "data", "telecom", "communication", "security system", "audio visual", "cabling"] },
  { icon: "Droplets", tone: "info", terms: ["plumbing", "plumber", "piping", "domestic water", "sanitary", "gas pipe", "gas piping"] },
  { icon: "AirVent", tone: "teal", terms: ["hvac", "ductwork", "duct work", "air conditioning", "ventilation"] },
  { icon: "Fan", tone: "teal", terms: ["mechanical"] },

  // ── Fire and life safety ───────────────────────────────────────────────
  { icon: "FireExtinguisher", tone: "red", terms: ["fire protection", "fire sprinkler", "sprinkler", "fire suppression", "fire alarm", "life safety", "standpipe"] },
  { icon: "SprayCan", tone: "red", terms: ["fireproofing", "fire proofing", "firestopping", "fire stopping", "fire caulk", "intumescent"] },

  // ── Conveyance ─────────────────────────────────────────────────────────
  { icon: "MoveVertical", tone: "neutral", terms: ["elevator", "escalator", "conveying", "conveyance", "vertical transport", "lift equipment"] },

  // ── Sitework ───────────────────────────────────────────────────────────
  // Before PAVING, so "Earthwork & Paving" reads as the earthwork it leads with.
  { icon: "Shovel", tone: "orange", terms: ["earthwork", "earth work", "excavation", "excavating", "grading", "sitework", "site work", "civil", "underground utilities", "shoring"] },
  { icon: "Trees", tone: "green", terms: ["landscaping", "landscape", "planting", "irrigation", "hardscape", "arborist"] },
  // "concrete paving" is spelled out because the single word "concrete" is
  // longer than "paving" and would otherwise win it for the structural slab.
  // Paving is sitework, and a flatwork crew is not a concrete subcontractor.
  { icon: "Cone", tone: "neutral", terms: ["paving", "concrete paving", "asphalt", "striping", "curb"] },
  { icon: "Fence", tone: "neutral", terms: ["fencing", "fence", "gate", "railing", "guardrail", "handrail"] },
  { icon: "Pickaxe", tone: "neutral", terms: ["demolition", "abatement", "asbestos", "selective demo"] },

  // ── General ────────────────────────────────────────────────────────────
  { icon: "HardHat", tone: "amber", terms: ["general condition", "general contractor", "general labor", "general requirement", "labor", "supervision", "safety"] },
  { icon: "Package", tone: "neutral", terms: ["specialty", "specialties", "equipment", "appliance", "furnishing", "signage", "window treatment"] },
  { icon: "Sparkles", tone: "teal", terms: ["final clean", "cleaning", "janitorial", "housekeeping"] },
];

/**
 * Observation type — Procore's "Category - Type" labels.
 *
 * The six FAMILY entries come first and carry the icons the card has always
 * drawn, so a company that simply lists "Safety" sees no change. Each sub-type
 * then adds a longer, higher-scoring term, which is how "Environmental - Spill"
 * stops being the tenth identical leaf.
 *
 * `DEFAULT_PROCORE_OBSERVATION_TYPE_OPTIONS` lists all 22 defaults, and
 * `propertyIcons.test.ts` holds this table to covering every one of them with
 * an icon no other default shares.
 *
 * Where a sub-type appears under TWO families — Procore has both
 * "Quality - Corrective Action" and "Safety - Corrective Action" — the term
 * names the family too, so each label matches exactly one entry.
 */
const OBSERVATION_TYPE_LEXICON: readonly PropertyIconLexiconEntry[] = [
  // ── The families themselves ────────────────────────────────────────────
  { icon: "Gauge", tone: "violet", family: "commissioning", fallback: true, terms: ["commissioning"] },
  { icon: "Leaf", tone: "green", family: "environmental", fallback: true, terms: ["environmental", "environment"] },
  { icon: "BadgeCheck", tone: "info", family: "quality", fallback: true, terms: ["quality"] },
  { icon: "ShieldAlert", tone: "red", family: "safety", fallback: true, terms: ["safety"] },
  { icon: "Hammer", tone: "orange", family: "work to complete", fallback: true, terms: ["work to complete"] },
  { icon: "Wrench", tone: "teal", family: "warranty", fallback: true, terms: ["warranty"] },

  // ── Environmental sub-types ────────────────────────────────────────────
  { icon: "Wind", tone: "green", family: "environmental", terms: ["air quality"] },
  { icon: "Landmark", tone: "green", family: "environmental", terms: ["cultural heritage", "heritage"] },
  { icon: "Biohazard", tone: "green", family: "environmental", terms: ["hazardous material", "hazmat"] },
  { icon: "Volume2", tone: "green", family: "environmental", terms: ["noise"] },
  { icon: "Waves", tone: "green", family: "environmental", terms: ["spill"] },
  { icon: "CloudRain", tone: "green", family: "environmental", terms: ["storm water", "stormwater"] },
  { icon: "Sprout", tone: "green", family: "environmental", terms: ["vegetation"] },
  { icon: "Recycle", tone: "green", family: "environmental", terms: ["waste management", "waste"] },
  { icon: "Bird", tone: "green", family: "environmental", terms: ["wildlife"] },

  // ── Quality sub-types ──────────────────────────────────────────────────
  { icon: "RotateCcw", tone: "info", family: "quality", terms: ["quality corrective"] },
  { icon: "ClipboardX", tone: "info", family: "quality", terms: ["deficiency", "deficient"] },
  { icon: "EqualNot", tone: "info", family: "quality", terms: ["non conformance", "nonconformance"] },
  { icon: "ListChecks", tone: "info", family: "quality", terms: ["pre punch", "prepunch"] },

  // ── Safety sub-types ───────────────────────────────────────────────────
  { icon: "Bandage", tone: "red", family: "safety", terms: ["safety corrective"] },
  { icon: "TriangleAlert", tone: "red", family: "safety", terms: ["near miss"] },
  { icon: "LifeBuoy", tone: "red", family: "safety", terms: ["preventative action", "preventive action"] },
  { icon: "OctagonAlert", tone: "red", family: "safety", terms: ["safety hazard"] },
  { icon: "Megaphone", tone: "red", family: "safety", terms: ["safety notice"] },
  { icon: "Siren", tone: "red", family: "safety", terms: ["safety violation"] },

  // ── Warranty sub-types ─────────────────────────────────────────────────
  { icon: "PhoneCall", tone: "teal", family: "warranty", terms: ["service call"] },

  /**
   * Not one of Procore's documented defaults, and the fourth most common type
   * in production anyway — 701 records on the staging database against 22 for
   * the whole Warranty family. A model-coordination clash: two elements
   * occupying one space, which is exactly what two overlapping circles draw.
   *
   * Its own family, so it keeps a tone of its own rather than borrowing
   * Commissioning's violet and blurring what the colour means.
   */
  { icon: "Blend", tone: "amber", family: "coordination", terms: ["clash", "interference"] },
];

/** Measurement and identifier names, including custom questions on Autodesk forms. */
const FIELD_NAME_LEXICON: readonly PropertyIconLexiconEntry[] = [
  { icon: "Ticket", tone: "orange", terms: ["ticket number", "ticket"] },
  { icon: "Barcode", tone: "info", terms: ["tracking number", "tracking"] },
  { icon: "FileDigit", tone: "violet", terms: ["reference", "identifier"] },
  { icon: "Hourglass", tone: "teal", terms: ["manpower hours", "crew hours", "worker hours", "labor hours", "hours per worker", "hours per person", "hours", "hour", "duration", "minutes", "time"] },
  { icon: "UsersRound", tone: "info", terms: ["worker", "people", "person", "crew", "headcount"] },
  { icon: "Timer", tone: "teal", terms: ["total hours", "hours operating", "operating hours", "runtime"] },
  { icon: "TimerOff", tone: "teal", terms: ["hours idle", "idle hours", "idle time", "downtime"] },
  { icon: "Banknote", tone: "orange", terms: ["hourly rate", "rate", "cost", "amount", "price", "budget"] },
  { icon: "PackagePlus", tone: "info", terms: ["quantity delivered", "delivered", "delivery", "received"] },
  { icon: "PackageMinus", tone: "orange", terms: ["quantity removed", "removed", "hauled"] },
  { icon: "Boxes", tone: "orange", terms: ["quantity", "count", "units"] },
  { icon: "Thermometer", tone: "orange", terms: ["temperature"] },
  { icon: "Ruler", tone: "violet", terms: ["length", "width", "height", "depth", "distance"] },
  { icon: "Weight", tone: "violet", terms: ["weight", "mass", "load"] },
];

export const PROPERTY_ICON_LEXICONS: Record<PropertyIconDomain, readonly PropertyIconLexiconEntry[]> = {
  trade: TRADE_LEXICON,
  observationType: OBSERVATION_TYPE_LEXICON,
  fieldName: FIELD_NAME_LEXICON,
};

/** The tones a monogram may be hashed into — every tone but the muted one. */
const MONOGRAM_TONES: readonly PropertyIconTone[] = ["info", "green", "violet", "orange", "teal", "red", "amber"];

export interface PropertyIconMark {
  kind: "icon";
  icon: PropertyIconName;
  tone: PropertyIconTone;
  /** The term that won, so a surprising mark can be explained without a debugger. */
  matched: string;
  /** The entry's family, where its vocabulary has them. */
  family?: string;
}

export interface PropertyMonogramMark {
  kind: "monogram";
  /** One or two letters off the value itself, not a claim about its meaning. */
  initials: string;
  tone: PropertyIconTone;
}

export type PropertyMark = PropertyIconMark | PropertyMonogramMark;

/**
 * Lowercase words, punctuation dropped.
 *
 * Trades arrive spelled every way a person types them — "Drywall/Paint",
 * "Civil / Sitework", "Glazing & Curtain Wall" — and the separator carries no
 * meaning, so it becomes a word break.
 */
function words(value: string): string[] {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().split(" ").filter(Boolean);
}

/**
 * How well `term` describes `valueWords`, or 0 for no match.
 *
 * A phrase beats a single word decisively (100 per word), and between two
 * terms of the same word count the longer spelling wins. So "Curtain Wall"
 * is glazing rather than a wall, and a two-word "concrete paving" takes a
 * label that the one-word "concrete" would otherwise have won on length
 * alone.
 *
 * Length is only a PROXY for specificity, and it inverts on a family word —
 * "environmental" is the longest and least specific thing in
 * "Environmental - Noise". That is what `fallback` exists to fix; see it.
 */
function score(term: string, valueWords: string[]): number {
  const termWords = term.split(" ");
  for (let i = 0; i + termWords.length <= valueWords.length; i++) {
    if (termWords.every((t, k) => valueWords[i + k]!.startsWith(t))) {
      return termWords.length * 100 + term.replaceAll(" ", "").length;
    }
  }
  return 0;
}

/** djb2 — a stable hash, so a value's tone is the same on every device and run. */
function hash(value: string): number {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * The mark for one property value, or `null` when there is no value.
 *
 * An empty property is NOT a monogram: the chip already says "Not set" beside
 * its field's name, and a mark invented for a blank would be the one thing on
 * the run that stands for nothing.
 */
export function resolvePropertyIcon(value: string, domain: PropertyIconDomain): PropertyMark | null {
  const valueWords = words(value);
  /**
   * A value the ASCII matcher cannot read is still A VALUE.
   *
   * `words()` keeps `[a-z0-9]` because that is what the terms are written in,
   * which leaves a trade spelled in another script with no words at all. That
   * used to return `null` — the same answer as an EMPTY field — so the chip
   * drew nothing. Fall back to the value's own letters for a monogram, and
   * reserve `null` for something with no letters or digits anywhere, which
   * genuinely has nothing to abbreviate.
   */
  if (!valueWords.length) {
    const letters = value.replace(/[^\p{L}\p{N}]+/gu, " ").trim();
    return letters ? monogramOf(letters.split(" ")) : null;
  }

  // The specific entries decide it; the fallbacks are consulted only if none
  // of them matched at all.
  for (const fallback of [false, true]) {
    let best: PropertyIconMark | null = null;
    let bestScore = 0;
    for (const entry of PROPERTY_ICON_LEXICONS[domain]) {
      if (!!entry.fallback !== fallback) continue;
      for (const term of entry.terms) {
        const s = score(term, valueWords);
        // Strictly greater: a tie leaves the earlier entry in place, which is
        // what makes the lexicon's order the documented tiebreak.
        if (s > bestScore) {
          bestScore = s;
          best = { kind: "icon", icon: entry.icon, tone: entry.tone, matched: term, family: entry.family };
        }
      }
    }
    if (best) return best;
  }

  return monogramOf(valueWords);
}

/** One or two letters off the value itself, in a tone hashed from the whole of it. */
function monogramOf(parts: string[]): PropertyMonogramMark {
  const initials = (parts.length > 1
    ? `${parts[0]![0]}${parts[1]![0]}`
    : parts[0]!.slice(0, 2)
  ).toUpperCase();
  return { kind: "monogram", initials, tone: MONOGRAM_TONES[hash(parts.join(" ")) % MONOGRAM_TONES.length]! };
}
